import React, { useCallback } from 'react';
import * as Haptics from 'expo-haptics';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { Button, theme, VStack } from '@sg/garnish';
import { PermissionsPrompt } from '@sg/shared-fe/features/permissions';

// ─────────────────────────────────────────────────────────────────────────────

export const PermissionsPrimerPushView = (props: Props) => {
  const { isRequesting, isSkipping, onAllow, onSkip } = props;

  const { formatMessage } = useIntl();

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleAllow = useCallback(() => {
    void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onAllow();
  }, [onAllow]);

  const handleSkip = useCallback(() => {
    void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onSkip();
  }, [onSkip]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <PermissionsPrompt.Title>
        <FormattedMessage {...messages.title} />
      </PermissionsPrompt.Title>

      <PermissionsPrompt.Text>
        <FormattedMessage {...messages.text} />
      </PermissionsPrompt.Text>

      <VStack gap={theme.spacing['4']}>
        <Button
          onPress={handleSkip}
          palette="cream"
          size="large-wide"
          isLoading={isSkipping}
          disabled={isRequesting}
        >
          {formatMessage(messages.notNow)}
        </Button>

        <Button
          onPress={handleAllow}
          palette="oatmeal"
          size="large-wide"
          disabled={isSkipping}
          isLoading={isRequesting}
        >
          {formatMessage(messages.allow)}
        </Button>
      </VStack>
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Get the most out of our app…',
    description: 'Permissions Primer | Push | Title',
  },
  text: {
    defaultMessage:
      'When you turn on push notifications, you’ll get surprise offers, menu updates and more.',
    description: 'Permissions Primer | Push | Body text',
  },
  notNow: {
    defaultMessage: 'Not now',
    description: 'Permissions Primer | Push | Skip CTA',
  },
  allow: {
    defaultMessage: 'Turn on notifications',
    description: 'Permissions Primer | Push | Allow CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isRequesting: boolean;
  isSkipping: boolean;
  onAllow: () => void;
  onSkip: () => void;
};
